import { useState } from "react";
import { BrBox } from "@/components/BrBox";
import { BrBtn } from "@/components/BrBtn";
import { BrTag } from "@/components/BrTag";
import { offsetShadow, TYPE } from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";

const PRO_MONTHLY_USD = 5.99;
const PRO_YEARLY_USD = 49;
const YEARLY_SAVINGS_USD = Math.round(PRO_MONTHLY_USD * 12 - PRO_YEARLY_USD);
const YEARLY_SAVINGS_PCT = Math.round(
  ((PRO_MONTHLY_USD * 12 - PRO_YEARLY_USD) / (PRO_MONTHLY_USD * 12)) * 100,
);

export function PricingSection({
  onStart,
  onStartPro,
}: {
  onStart: () => void;
  /** Pro CTA override — signed-in users go to checkout instead of signup. */
  onStartPro?: () => void;
}) {
  const p = useTheme();
  const { isMobile } = useViewport();
  const [billing, setBilling] = useState<"monthly" | "yearly">("yearly");

  const plans = [
    {
      id: "free",
      name: "FREE",
      price: "$0",
      sub: "forever",
      tagline: "Everything you need to get started.",
      features: [
        { ok: true, t: "Sync new games from chess.com + Lichess" },
        { ok: true, t: "Last 50 games analyzed at signup (200 across accounts)" },
        { ok: true, t: "Depth 15 engine analysis" },
        { ok: true, t: "Unlimited training sessions" },
        { ok: true, t: "3 custom routines" },
        { ok: true, t: "Endless training mode" },
        { ok: false, t: "Full game history" },
        { ok: false, t: "Depth 22 engine analysis" },
        { ok: false, t: "Unlimited custom routines" },
        { ok: false, t: "Priority sync every 15 min" },
      ],
      cta: "Start free",
      ctaVariant: "surface" as const,
      featured: false,
    },
    {
      id: "pro",
      name: "PRO",
      priceMo: `$${PRO_MONTHLY_USD}`,
      priceYr: `$${PRO_YEARLY_USD}`,
      sub: billing === "yearly" ? `per year · saves $${YEARLY_SAVINGS_USD}` : "per month",
      tagline: "The full machine.",
      features: [
        { ok: true, t: "Everything in Free" },
        { ok: true, t: "Full game history" },
        { ok: true, t: "Depth 22 engine analysis" },
        { ok: true, t: "Deep per-position server analysis (coming soon)" },
        { ok: true, t: "Unlimited custom routines" },
        { ok: true, t: "Priority sync every 15 min" },
      ],
      cta:
        billing === "yearly"
          ? `Start Pro · $${PRO_YEARLY_USD}/yr`
          : `Start Pro · $${PRO_MONTHLY_USD}/mo`,
      ctaVariant: "accent" as const,
      featured: true,
    },
  ];

  return (
    <section id="pricing" style={{ padding: isMobile ? "48px 16px" : "80px 40px", maxWidth: 1240, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: isMobile ? 28 : 40 }}>
        <BrTag>PRICING</BrTag>
        <h2
          style={{
            fontFamily: TYPE.display,
            fontSize: isMobile ? 44 : 64,
            fontWeight: 800,
            letterSpacing: isMobile ? -1.5 : -2.5,
            lineHeight: 0.95,
            margin: "14px 0 12px",
            textTransform: "uppercase",
          }}
        >
          TWO PLANS.
          <br />
          <span style={{ background: p.accent, color: p.accentFg, padding: "0 8px", border: `3px solid ${p.border}`, boxShadow: offsetShadow(p, 5, 5), display: "inline-block" }}>
            NO BS.
          </span>
        </h2>
        <p style={{ fontSize: isMobile ? 14 : 16, color: p.fgMuted, lineHeight: 1.5, maxWidth: 540, margin: "0 auto", fontWeight: 500 }}>
          Free is enough to know whether this works on you. Pro is for when you&rsquo;re convinced.
        </p>
        <div style={{ display: "inline-flex", marginTop: 24, border: `2px solid ${p.border}`, background: p.card, boxShadow: offsetShadow(p, 4, 4) }}>
          {[
            { id: "monthly" as const, label: "MONTHLY" },
            { id: "yearly" as const, label: `YEARLY · SAVE ${YEARLY_SAVINGS_PCT}%` },
          ].map((opt) => {
            const active = billing === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setBilling(opt.id)}
                style={{
                  background: active ? p.fg : "transparent",
                  color: active ? p.bg : p.fg,
                  border: "none",
                  padding: "10px 18px",
                  fontFamily: TYPE.body,
                  fontSize: 12,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  cursor: "pointer",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: isMobile ? 16 : 24,
          maxWidth: 900,
          margin: "0 auto",
        }}
      >
        {plans.map((plan) => {
          const isPro = plan.id === "pro";
          const displayedPrice = isPro ? (billing === "yearly" ? plan.priceYr : plan.priceMo) : plan.price;
          return (
            <div key={plan.id} style={{ position: "relative" }}>
              {plan.featured && (
                <div
                  style={{
                    position: "absolute",
                    top: -16,
                    left: "50%",
                    transform: "translateX(-50%) rotate(-2deg)",
                    background: p.danger,
                    color: p.dangerFg,
                    border: `2px solid ${p.border}`,
                    padding: "6px 14px",
                    fontFamily: TYPE.body,
                    fontWeight: 800,
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    boxShadow: offsetShadow(p, 3, 3),
                    zIndex: 2,
                  }}
                >
                  ★ MOST POPULAR
                </div>
              )}
              <BrBox padding={isMobile ? 24 : 32} shadowSize={plan.featured ? 10 : 6} bg={plan.featured ? p.fg : p.card} fg={plan.featured ? p.bg : p.fg} style={{ height: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                  <div>
                    <div style={{ fontFamily: TYPE.display, fontSize: 28, fontWeight: 800, letterSpacing: -0.8, textTransform: "uppercase" }}>{plan.name}</div>
                    <div style={{ fontSize: 13, opacity: 0.75, marginTop: 4, lineHeight: 1.4 }}>{plan.tagline}</div>
                  </div>
                  {plan.featured && <div style={{ fontSize: 28, lineHeight: 1 }}>♛</div>}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 18, marginBottom: 6 }}>
                  <span style={{ fontFamily: TYPE.display, fontSize: isMobile ? 56 : 72, fontWeight: 800, letterSpacing: -3, lineHeight: 0.9 }}>{displayedPrice}</span>
                  <span style={{ fontSize: 13, fontFamily: TYPE.mono, fontWeight: 700, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.5 }}>{plan.sub}</span>
                </div>
                <BrBtn
                  variant={plan.ctaVariant}
                  size="lg"
                  full
                  onClick={isPro && onStartPro ? onStartPro : onStart}
                  style={{ marginTop: 18, marginBottom: 22 }}
                >
                  {plan.cta} →
                </BrBtn>
                <div style={{ display: "flex", flexDirection: "column", gap: 9, paddingTop: 18, borderTop: `2px solid ${plan.featured ? p.bg : p.border}` }}>
                  {plan.features.map((f, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13.5, fontWeight: 600, lineHeight: 1.4, opacity: f.ok ? 1 : 0.45 }}>
                      <span
                        style={{
                          width: 18,
                          height: 18,
                          flexShrink: 0,
                          marginTop: 1,
                          background: f.ok ? (plan.featured ? p.accent : p.success) : "transparent",
                          color: f.ok ? (plan.featured ? p.accentFg : p.successFg) : "inherit",
                          border: `2px solid ${plan.featured ? p.bg : p.border}`,
                          display: "grid",
                          placeItems: "center",
                          fontFamily: TYPE.mono,
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {f.ok ? "✓" : "—"}
                      </span>
                      <span style={{ textDecoration: f.ok ? "none" : "line-through" }}>{f.t}</span>
                    </div>
                  ))}
                </div>
              </BrBox>
            </div>
          );
        })}
      </div>

      <div style={{ textAlign: "center", marginTop: 28, fontSize: 12, fontFamily: TYPE.mono, fontWeight: 700, color: p.fgMuted, letterSpacing: 0.8, textTransform: "uppercase" }}>
        Cancel anytime · 30-day money-back · No upsells
      </div>
    </section>
  );
}
